import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ServiceTierCard from './components/ServiceTierCard';
import ServiceConfigurator from './components/ServiceConfigurator';
import MethodologyShowcase from './components/MethodologyShowcase';
import TestimonialSection from './components/TestimonialSection';
import ROICalculator from './components/ROICalculator';
import ConsultationBooking from './components/ConsultationBooking';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ServicesImplementation = () => {
  const serviceTiers = [
    {
      name: "Strategic Assessment",
      subtitle: "C-Suite Focused Planning",
      icon: "Target",
      pricing: "Starting at $75K",
      pricingNote: "Fixed-scope engagement",
      timeline: "6-8 weeks",
      teamSize: "3-4 experts",
      ctaText: "Start Strategic Planning",
      features: [
        "Comprehensive AI readiness assessment",
        "Custom strategic roadmap development",
        "Executive stakeholder workshops",
        "Technology stack recommendations",
        "Risk assessment and mitigation planning",
        "ROI projections and business case",
        "Change management strategy",
        "Governance framework design"
      ]
    },
    {
      name: "Technical Implementation",
      subtitle: "Hands-On Development",
      icon: "Code",
      pricing: "Starting at $150K",
      pricingNote: "Value-based investment",
      timeline: "12-16 weeks",
      teamSize: "5-8 experts",
      ctaText: "Begin Implementation",
      features: [
        "Full-stack AI solution development",
        "Open-source technology integration",
        "Custom model training and optimization",
        "API development and integration",
        "Security and compliance implementation",
        "Performance monitoring setup",
        "Team training and knowledge transfer",
        "Production deployment and support"
      ]
    },
    {
      name: "Ongoing Optimization",
      subtitle: "Continuous Improvement",
      icon: "TrendingUp",
      pricing: "Starting at $25K/month",
      pricingNote: "Flexible monthly engagement",
      timeline: "Ongoing",
      teamSize: "2-3 experts",
      ctaText: "Optimize Performance",
      features: [
        "Performance monitoring and tuning",
        "Model retraining and updates",
        "Infrastructure scaling optimization",
        "Cost optimization analysis",
        "New feature development",
        "Security updates and patches",
        "Team coaching and mentoring",
        "Strategic technology guidance"
      ]
    }
  ];

  const implementationConcerns = [
    {
      concern: "Change Management",
      description: "How do we ensure smooth adoption across the organization?",
      solution: "We provide comprehensive change management support including stakeholder engagement, communication strategies, and phased rollout plans that minimize disruption while maximizing adoption."
    },
    {
      concern: "Team Training",
      description: "Will our team be able to maintain and extend the AI systems?",
      solution: "Knowledge transfer is built into every engagement. We provide hands-on training, documentation, and ongoing mentoring to ensure your team becomes self-sufficient with the new AI capabilities."
    },
    {
      concern: "Technology Integration",
      description: "How will AI solutions integrate with our existing systems?",
      solution: "Our integration-first approach ensures seamless connectivity with your current technology stack. We design APIs and interfaces that work with your existing workflows and data sources."
    },
    {
      concern: "Security & Compliance",
      description: "Can we maintain security and regulatory compliance?",
      solution: "Security and compliance are foundational to our approach. We implement enterprise-grade security measures and ensure all solutions meet your industry\'s regulatory requirements from day one."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Services & Implementation - AI Clean Core</title>
        <meta name="description" content="Custom AI transformation services with proven methodologies. Strategic assessment, technical implementation, and ongoing optimization for enterprise AI independence." />
      </Helmet>
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-brand-primary via-brand-primary to-brand-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Custom AI Transformation
              <span className="block text-brand-secondary">That Actually Works</span>
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Move beyond one-size-fits-all solutions. Our proven methodology delivers measurable results 
              through strategic planning, hands-on implementation, and continuous optimization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                className="bg-white text-brand-primary hover:bg-gray-50"
                iconName="Calculator"
                iconPosition="left"
              >
                Calculate Your ROI
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-brand-primary"
                iconName="Calendar"
                iconPosition="right"
              >
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Service Tiers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Three Engagement Models, One Goal
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Whether you need strategic direction, hands-on implementation, or ongoing optimization, 
              we have the right engagement model for your AI transformation journey.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {serviceTiers?.map((tier, index) => (
              <ServiceTierCard 
                key={index} 
                tier={tier} 
                isPopular={index === 1} 
              />
            ))}
          </div>

          <div className="text-center">
            <p className="text-text-secondary mb-6">
              Not sure which service is right for you? Let our configurator help you decide.
            </p>
            <Button
              variant="outline"
              iconName="Settings"
              iconPosition="left"
            >
              Use Service Configurator
            </Button>
          </div>
        </div>
      </section>
      {/* Service Configurator */}
      <section className="py-20 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Find Your Perfect Engagement Model
            </h2>
            <p className="text-xl text-text-secondary">
              Answer a few questions to get a personalized service recommendation based on your 
              current AI maturity, organizational needs, and transformation goals.
            </p>
          </div>

          <ServiceConfigurator />
        </div>
      </section>
      {/* Methodology Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Our Proven Implementation Methodology
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Four phases, measurable outcomes, and a 94% success rate. See exactly how we transform 
              organizations from AI-curious to AI-independent.
            </p>
          </div>

          <MethodologyShowcase />
        </div>
      </section>
      {/* Implementation Concerns */}
      <section className="py-20 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Common Implementation Concerns
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              We've addressed these challenges hundreds of times. Here's how we ensure your AI transformation succeeds.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {implementationConcerns?.map((item, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="HelpCircle" size={24} color="var(--color-brand-primary)" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {item?.concern}
                    </h3>
                    <p className="text-text-secondary mb-4 text-sm">
                      {item?.description}
                    </p>
                    <div className="bg-brand-surface rounded-lg p-4">
                      <h4 className="font-medium text-brand-primary mb-2 flex items-center">
                        <Icon name="CheckCircle" size={16} color="var(--color-brand-primary)" className="mr-2" />
                        Our Solution:
                      </h4>
                      <p className="text-text-secondary text-sm">
                        {item?.solution}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Client Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TestimonialSection />
        </div>
      </section>
      {/* ROI Calculator */}
      <section className="py-20 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Calculate Your Investment Return
            </h2>
            <p className="text-xl text-text-secondary">
              See the potential financial impact of AI Clean Core services on your organization. 
              Get personalized ROI projections based on your industry and current AI spending.
            </p>
          </div>

          <ROICalculator />
        </div>
      </section>
      {/* Consultation Booking */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Ready to Start Your AI Transformation?
            </h2>
            <p className="text-xl text-text-secondary">
              Schedule a free consultation with our AI strategy experts. No sales pitch—just actionable 
              insights tailored to your organization's unique needs and goals.
            </p>
          </div>

          <ConsultationBooking />
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-primary to-brand-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Stop Renting AI. Start Owning It.
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Every day you delay AI independence is another day of vendor dependency, escalating costs, 
            and missed competitive advantages. The question isn't whether to act—it's how quickly you can start.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              className="bg-white text-brand-primary hover:bg-gray-50"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Get Started Today
            </Button>
            <Button
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-brand-primary"
              iconName="Download"
              iconPosition="left"
            >
              Download AI Blueprint
            </Button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xl font-bold">AI Clean Core</div>
                  <div className="text-sm text-gray-400">Enterprise AI Independence</div>
                </div>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Helping organizations achieve AI independence through open-source solutions and strategic implementation.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Strategic Assessment</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Technical Implementation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Ongoing Optimization</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Case Studies</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Resources</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date()?.getFullYear()} AI Clean Core. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesImplementation;